export const Condition = {
    BETWEEN: "BETWEEN",
    IN: "IN",
    CONTAINS: "CONTAINS",
    EQUAL: "EQUAL",
};

export const Operator = {
    AND: "AND",
    OR: "OR",
    NOT: "NOT",
};

export const SimplerCriterionToComplexCriterionConverter = (simpleCriterion) => {
    let audienceCriterionComputed = {
        criterion: [[]],
        operator: "OR",
        isCriterionNegated: false,
    }
    let criterionList = []
    simpleCriterion.forEach((element) => {
        let column = element['column']
        let values = element['values']
        if (values.length > 0) {
            const currObj = {
                column: column,
                condition: "IN",
                values: values,
                lowerbound: null,
                upperbound: null,
                singleValue: null,
            }
            criterionList.push(currObj);
        }
    });

    if (criterionList.length == 0) {
        audienceCriterionComputed['criterion'] = []
    }
    else {
        audienceCriterionComputed['criterion'][0] = criterionList
    }
    return audienceCriterionComputed;
};

export const ComplexCriterionToSimpleCriterionConverter = (complexCriterion) => {
    let criterionList = complexCriterion['criterion'][0]
    let simpleCriterion = []

    criterionList.forEach((element) => {
        let column = element['column']
        let values = element['values']
        const currObj = {
            'column': column,
            'values': values,
        }
        simpleCriterion.push(currObj);
    });
    return simpleCriterion;
};

export const findCriterionTypeFromAudience = (audience) => {
    if (audience.parent_audiences != "") {
        return "Aggregate"
    }
    let query = JSON.parse(audience.audience_criterion_json)
    
    // Add null/undefined checks for query and query.criterion
    if (!query || !query.criterion) {
        return "Simple" // Default to Simple if criterion is missing
    }
    
    if (query.criterion.operator == "not") {
        return "Complex"
    }
    
    // Check if operands exists before accessing it
    if (query.criterion.operands && query.criterion.operands.some(operand => 'operands' in operand)) {
        return "Complex"
    }
    
    return "Simple"
};

export const simpleCriterionObjectBuilder = (query) => {
    // Add null/undefined checks
    if (!query || !query.criterion) {
        return [];
    }
    
    // Handle single leaf criterion (no operands wrapper)
    // e.g. { criterion: { column_name: "gender", operator: "in", values: ["M"] } }
    if (query.criterion.column_name) {
        return [{
            column: query.criterion.column_name,
            values: query.criterion.values || [],
        }];
    }
    
    // Handle compound criterion with operands array
    if (!query.criterion.operands) {
        return [];
    }
    const simpleCriterionValues = query.criterion.operands.map((element) => {
        return {
            column: element['column_name'],
            values: element['values'],
        };
    });
    return simpleCriterionValues;
};

// Helper function to convert a leaf criterion to the UI criterion object format
const buildCriterionObject = (criteria) => {
    let obj = {
        column: null,
        condition: null,
        values: [],
        lowerbound: null,
        upperbound: null,
        singleValue: null,
    }
    obj.column = criteria.column_name;
    if (criteria.operator == "in") {
        obj.condition = Condition.IN
        obj.values = criteria.values || []
    }
    else if (criteria.operator == "equal") {
        obj.condition = Condition.EQUAL
        obj.singleValue = (criteria.values || [])[0] ?? null
    }
    else if (criteria.operator == "between") {
        obj.condition = Condition.BETWEEN
        obj.lowerbound = criteria.lower_bound
        obj.upperbound = criteria.upper_bound
    }
    else {
        // Default to IN for unknown operators
        obj.condition = Condition.IN
        obj.values = criteria.values || []
    }
    return obj;
}

// Helper function to recursively extract all leaf criteria from a nested structure
const extractLeafCriteria = (node) => {
    let results = [];
    
    // Check if this is a leaf criterion (has column_name)
    if (node.column_name) {
        results.push(buildCriterionObject(node));
        return results;
    }
    
    // If it has operands, recursively process them
    if (node.operands && Array.isArray(node.operands)) {
        node.operands.forEach(operand => {
            results = results.concat(extractLeafCriteria(operand));
        });
    }
    
    return results;
}

export const complexCriterionObjectBuilder = (query) => {

    let audienceCriterion = {
        criterion: [],
        operator: "OR",
        isCriterionNegated: false,
    }

    // Add null/undefined checks
    if (!query || !query.criterion) {
        return audienceCriterion;
    }

    if (query.criterion.operator == "not") {
        audienceCriterion.isCriterionNegated = true;
        let operands = query.criterion.operand?.operands
        let operator = query.criterion.operand?.operator
        if (operands && operator) {
            query = {
                criterion: {
                    "operands": operands,
                    "operator": operator
                }
            }
        }
    }
    
    if (query.criterion.operator) {
        audienceCriterion.operator = query.criterion.operator.toUpperCase()
    }

    if (!query.criterion.operands) {
        // Handle single leaf criterion at the top level
        if (query.criterion.column_name) {
            audienceCriterion.criterion.push([buildCriterionObject(query.criterion)]);
        }
        return audienceCriterion;
    }

    // Process each top-level operand as a criterion group
    query.criterion.operands.forEach((criterionGroup) => {
        // Check if this is a leaf criterion (has column_name directly)
        if (criterionGroup.column_name) {
            // Single leaf criterion - wrap in its own group
            audienceCriterion.criterion.push([buildCriterionObject(criterionGroup)]);
            return;
        }
        
        let currentCriterionGroup = []
        // Recursively process criterion group to handle nested structures
        processCriterionGroupRecursive(criterionGroup, currentCriterionGroup);
        
        if (currentCriterionGroup.length > 0) {
            audienceCriterion.criterion.push(currentCriterionGroup);
        }
    })
    return audienceCriterion;
};

// Helper function to recursively process criterion groups
const processCriterionGroupRecursive = (criterionGroup, currentCriterionGroup) => {
    if (!criterionGroup.operands) return;
    
    criterionGroup.operands.forEach((criteria) => {
        // Check if this is a nested group (has operands) or a leaf criterion
        if (criteria.operands) {
            // This is a nested group - recursively process it
            processCriterionGroupRecursive(criteria, currentCriterionGroup);
        } else {
            // This is a leaf criterion - process it
            let obj = {
                column: null,
                condition: null,
                values: [],
                lowerbound: null,
                upperbound: null,
                singleValue: null,
            }
            obj.column = criteria.column_name;
            if (criteria.operator == "in") {
                obj.condition = Condition.IN
                obj.values = criteria.values
            }
            else if (criteria.operator == "between") {
                obj.condition = Condition.BETWEEN
                obj.lowerbound = criteria.lower_bound
                obj.upperbound = criteria.upper_bound
            }
            // Only push if condition was set (skip invalid criteria)
            if (obj.condition) {
                currentCriterionGroup.push(obj);
            }
        }
    });
};

export const histogramQueryBuilder = (
    audienceCriterion,
    additionalHistogramAttributes
) => {
    let query = audienceCriterionQueryBuilder(audienceCriterion);
    if (additionalHistogramAttributes?.histogramColumns?.length > 0) {
        query["attribute_names"] = additionalHistogramAttributes.histogramColumns;
    }
    return query;
};

export const audienceCriterionQueryBuilder = (audienceCriterion) => {
    if (audienceCriterion.criterion.length == 0) {
        return {};
    }
    const outerCondition = audienceCriterion.operator.toLowerCase();
    const innerCondition =
        audienceCriterion.operator == Operator.AND ? "or" : "and";
    let query = {
        criterion: {
            operands: [],
            operator: outerCondition,
        },
    };
    audienceCriterion.criterion.forEach((criterionGroup) => {
        query.criterion.operands.push(
            criterionGroupQueryBuilder(criterionGroup, innerCondition)
        );
    });

    if (audienceCriterion.isCriterionNegated) {
        let negateCriterion = query.criterion;
        query.criterion = {
            operand: negateCriterion,
            operator: "not",
        };
    }

    return query;
};

const criterionGroupQueryBuilder = (criterionGroup, innerCondition) => {
    let query = {
        operands: [],
        operator: innerCondition,
    };

    criterionGroup.forEach((criterion) => {
        query.operands.push(criterionQueryBuilder(criterion));
    });

    return query;
};

const criterionQueryBuilder = (criterion) => {
    // Default to IN condition if not specified
    let operator = criterion.condition || Condition.IN;
    if (operator == Condition.EQUAL) {
        operator = Condition.IN;
    }
    
    const conditionProperties = {
        IN: { values: criterion.values || [] },
        EQUAL: { values: [criterion.singleValue] },
        BETWEEN: {
            lower_bound: criterion.lowerbound,
            upper_bound: criterion.upperbound,
        },
    };
    
    const result = {
        column_name: criterion.column,
        operator: operator.toLowerCase(),
        ...conditionProperties[operator],
    };
    return result;
};